<script setup lang="ts">
  import { useFieldValidation } from '@/composables/useQuestionnaire'
  import { useTranslation } from '@/composables/useTranslation'
  import InputField from '@/components/admin/form/InputField.vue'
  import { onMounted } from 'vue'

  const { t } = useTranslation({
    en: {
      enterAnswerHere: 'Enter your answer here...',
      enterEmailHere: 'Enter your email address here...',
      enterPhoneHere: 'Enter your phone number here...',
      enterNumberHere: 'Enter a number here...',
      enterUrlHere: 'Enter a URL here...'
    },
    de: {
      enterAnswerHere: 'Geben Sie hier Ihre Antwort ein...',
      enterEmailHere: 'Geben Sie hier Ihre E-Mail-Adresse ein...',
      enterPhoneHere: 'Geben Sie hier Ihre Telefonnummer ein...',
      enterNumberHere: 'Geben Sie hier eine Zahl ein...',
      enterUrlHere: 'Geben Sie hier eine URL ein...'
    }
  })

  const props = defineProps({
    field: {
      type: Object,
      required: true
    },
    modelValue: {
      type: [String, Number],
      default: ''
    },
    isPreviewMode: {
      type: Boolean,
      default: false
    },
    descriptionId: {
      type: String,
      required: false
    }
  })

  const emit = defineEmits(['update:modelValue', 'validation-error'])

  const { validationError, handleInputChange, validate } = useFieldValidation(props, emit)

  onMounted(() => {
    const inputElement = document.querySelector(
      `input[name="${props.field.uuid}"]`
    ) as HTMLInputElement
    if (inputElement) {
      inputElement.focus()
    }
  })

  function validateCurrentValue() {
    const result = validate(props.modelValue)
    validationError.value = result.errors[0] || ''
    emit('validation-error', !result.valid)
  }

  function getInputType(fieldType: string): string {
    switch (fieldType) {
      case 'emailinput':
        return 'email'
      case 'phoneinput':
        return 'tel'
      case 'numberinput':
        return 'number'
      case 'decimalnumberinput':
        return 'text' // Use text to support German comma decimal separator
      case 'urlinput':
        return 'url'
      case 'timefield':
        return 'time'
      default:
        return 'text'
    }
  }

  function getPlaceholder(fieldType: string): string {
    switch (fieldType) {
      case 'emailinput':
        return t('enterEmailHere')
      case 'phoneinput':
        return t('enterPhoneHere')
      case 'numberinput':
        return t('enterNumberHere')
      case 'urlinput':
        return t('enterUrlHere')
      default:
        return t('enterAnswerHere')
    }
  }
</script>

<template>
  <InputField
    :id="field.uuid"
    :name="field.uuid"
    :model-value="modelValue"
    :type="getInputType(field.type)"
    :placeholder="getPlaceholder(field.type)"
    :disabled="isPreviewMode"
    :show-icon="true"
    :error="validationError"
    :description-id="descriptionId"
    class="w-full"
    :data-cy="`InputBoxV3Input-${field.type}-${field.uuid}`"
    @update:model-value="handleInputChange"
    @blur="validateCurrentValue"
  />
</template>
